import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  AlertTriangle,
  Calculator,
  MapPin,
  Pencil,
  Rows2,
  Table2,
  ArrowLeft,
  Undo2,
  Scale,
  SlidersHorizontal,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Sheet, SheetContent } from '@/components/atoms/sheet';
import { Fullbleed } from '@/contexts/fullbleed';
import { EntityIndexHeader } from '@/components/layouts';
import { EntityViewToggle } from '@/components/molecules';
import { useDebounce } from '@/hooks/useDebounce';
import { useTaxZones } from '@/hooks/taxes/useTaxZones';
import { useTaxGroups } from '@/hooks/taxes/useTaxGroups';
import { useTaxOverrides } from '@/hooks/taxes/useTaxOverrides';
import type { TaxZone } from '@/types/tax';
import { TaxZoneListPanel, type StatusFilter } from '@/components/organisms/taxes/TaxZoneListPanel';
import { TaxCountyPanel } from '@/components/organisms/taxes/TaxCountyPanel';
import { TaxCityPanel } from '@/components/organisms/taxes/TaxCityPanel';
import { TaxZoneDetail } from '@/components/organisms/taxes/TaxZoneDetail';
import { TaxZoneTableView } from '@/components/organisms/taxes/TaxZoneTableView';
import { TaxTypesTableView } from '@/components/organisms/taxes/TaxTypesTableView';
import { TaxBusinessUnitsPanel } from '@/components/organisms/taxes/TaxBusinessUnitsPanel';
import { TaxZipLookupDialog } from '@/components/organisms/taxes/TaxZipLookupDialog';
import { PolygonTaxZoneDialog } from '@/components/organisms/taxes/PolygonTaxZoneDialog';
import { ManualTaxZoneDialog } from '@/components/organisms/taxes/ManualTaxZoneDialog';
import {
  idOf,
  countyDisplayLabel,
  isProvincialCountyLabel,
} from '@/components/organisms/taxes/support/tax-helpers';

// ─── Route ───────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_dashboard/dashboard/taxes')({
  component: TaxesPage,
});

// ─── View mode ────────────────────────────────────────────────────────────────

// 'list' is the default flat "all taxes" table and is always the landing view.
// 'split' and 'table' are the State→County→City geo drill-downs, reached via
// the "Advanced" toggle.
type ViewMode = 'list' | 'split' | 'table';
type AdvancedMode = 'split' | 'table';

// Remembers which geo view the user last used so the "Advanced" button
// returns them to it (split vs. table) instead of always defaulting to split.
// The top-level list-vs-advanced choice is intentionally NOT persisted: the
// flat tax list is always the entry point on a fresh load.
function getStoredAdvancedMode(): AdvancedMode {
  try {
    return localStorage.getItem('taxes-advanced-view') === 'table' ? 'table' : 'split';
  } catch {
    return 'split';
  }
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function TaxesPage() {
  // Keep TaxesIndexPage mounted even when on a sub-route
  // (/dashboard/taxes/simulator or /overrides) so back-nav restores the
  // user's hierarchy selection, search filter, and scroll position
  // instead of resetting. Hiding via `display:none` preserves React
  // state, React Query caches, and any open dialogs (which portal to
  // body anyway). The sub-route's `<Outlet />` renders the active child;
  // its `<Fullbleed />` switches the dashboard shell into full-canvas
  // mode so the hidden index doesn't bleed through visually.
  const { location } = useRouterState();
  const isIndexRoute = /^\/dashboard\/taxes\/?$/.test(location.pathname);

  return (
    <>
      <div className={isIndexRoute ? 'contents' : 'hidden'}>
        <TaxesIndexPage />
      </div>
      {!isIndexRoute && <Outlet />}
    </>
  );
}

function TaxesIndexPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [mobileCountySheetOpen, setMobileCountySheetOpen] = useState(false);
  const [mobileCitySheetOpen, setMobileCitySheetOpen] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [manualZoneOpen, setManualZoneOpen] = useState(false);
  const [polygonOpen, setPolygonOpen] = useState(false);

  const { data: zones = [] } = useTaxZones();
  const { data: groups = [] } = useTaxGroups();
  // NOT-130 — header badge "View overrides (N)" links to the Overrides
  // Viewer with the live count. Hidden when zero so the header stays
  // uncluttered for tenants who never override anything.
  const { data: overridesData } = useTaxOverrides();
  const overrideCount = overridesData?.overrides.length ?? 0;

  // ── Hoisted filters ───────────────────────────────────────────────────────
  // The status chip + search input live in Layer 1 (TaxZoneListPanel) but
  // need to cascade into Layer 2 / 3 (counties + cities) so the user sees
  // a coherent narrowing of the whole hierarchy. NOT-9 reproduced as:
  // selecting "Pending" emptied Panel 1 but the BC · Counties panel still
  // showed its (current) zones because they came from raw `zones` instead
  // of the filtered set.
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const search = useDebounce(searchInput, 200);

  const filteredZones = useMemo(() => {
    const q = search.trim().toLowerCase();
    return zones.filter((z) => {
      if (statusFilter !== 'all' && z.refreshStatus !== statusFilter) return false;
      if (!q) return true;
      return (
        z.zipCode?.toLowerCase().includes(q) ||
        z.city?.toLowerCase().includes(q) ||
        z.stateCode?.toLowerCase().includes(q)
      );
    });
  }, [zones, search, statusFilter]);

  // Auto-select the first state when zones load and nothing is selected,
  // OR when the current selection drops out of the filtered set (e.g.
  // the user clicked "Populating" while AB was selected and AB has no
  // populating zones — pre-2026-06-16 the right panel kept showing AB
  // even though the left list no longer contained it).
  useEffect(() => {
    const filteredStates = new Set(filteredZones.map((z) => z.stateCode).filter(Boolean));
    if (selectedStateCode && filteredStates.has(selectedStateCode)) return;
    const firstState =
      filteredZones.find((z) => z.stateCode)?.stateCode ??
      zones.find((z) => z.stateCode)?.stateCode ??
      null;
    if (firstState !== selectedStateCode) {
      setSelectedStateCode(firstState);
    }
  }, [filteredZones, zones, selectedStateCode]);

  // Auto-select the first county when state changes, or when zones refetch
  // invalidates the current selection. Keeps current county if still present.
  // NOTE: we match on `countyDisplayLabel(z)` rather than the raw `z.county`
  // so Canadian zones (county === null → "Provincial" label) still navigate
  // correctly through the hierarchy [NOT-4].
  useEffect(() => {
    if (!selectedStateCode) return;
    const counties = zones
      .filter((z) => z.stateCode === selectedStateCode)
      .map((z) => countyDisplayLabel(z));
    if (!selectedCounty || !counties.includes(selectedCounty)) {
      setSelectedCounty(counties[0] ?? null);
      setSelectedCity(null);
    }
  }, [selectedStateCode, selectedCounty, zones]);

  // Auto-select the first city when county changes, or when zones refetch
  // invalidates the current selection. Keeps current city if still present.
  useEffect(() => {
    if (!selectedStateCode || !selectedCounty) return;
    const cities = zones
      .filter(
        (z) =>
          z.stateCode === selectedStateCode && countyDisplayLabel(z) === selectedCounty && z.city,
      )
      .map((z) => z.city!);
    if (!selectedCity || !cities.includes(selectedCity)) {
      setSelectedCity(cities[0] ?? null);
    }
  }, [selectedCounty, selectedStateCode, selectedCity, zones]);

  const groupsById = useMemo(() => new Map(groups.map((g) => [idOf(g), g])), [groups]);

  const zipsByGroup = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const zone of zones) {
      const gId = zone.taxGroupId ?? (zone.taxGroup ? idOf(zone.taxGroup) : null);
      if (!gId || !zone.zipCode) continue;
      if (!map.has(gId)) map.set(gId, []);
      map.get(gId)!.push(zone.zipCode);
    }
    return map;
  }, [zones]);

  const pendingZones = useMemo(
    () => zones.filter((z: TaxZone) => z.refreshStatus === 'pending_review'),
    [zones],
  );

  // The County / City panels both consume `filteredZones` so a status
  // chip or search query applied in Layer 1 narrows the entire hierarchy,
  // not just the state list. (Pre-fix: the panels received raw `zones`,
  // so filtering Layer 1 had no effect on Layers 2/3 — NOT-9.)
  const stateZones = useMemo(
    () => filteredZones.filter((z) => z.stateCode === selectedStateCode),
    [filteredZones, selectedStateCode],
  );

  const countyZones = useMemo(
    () => stateZones.filter((z) => countyDisplayLabel(z) === selectedCounty),
    [stateZones, selectedCounty],
  );

  function handleSetViewMode(mode: ViewMode) {
    setViewMode(mode);
    // Remember the last geo view so "Advanced" restores it next time. The
    // top-level list/advanced choice is not persisted (see getStoredAdvancedMode).
    if (mode === 'split' || mode === 'table') {
      try {
        localStorage.setItem('taxes-advanced-view', mode);
      } catch {
        // ignore
      }
    }
  }

  function handleSelectState(code: string) {
    setSelectedStateCode(code);
    setSelectedCounty(null);
    setSelectedCity(null);
  }

  function handleSelectCounty(county: string) {
    setSelectedCounty(county);
    setSelectedCity(null);
  }

  function handleSelectCity(city: string) {
    setSelectedCity(city);
  }

  function handleOpenDetail(id: string) {
    setSelectedZoneId(id);
  }

  // NOT-130 — quick link to the Overrides Viewer. Hidden when zero
  // overrides exist so the header stays clean for default tenants.
  const OverridesLink =
    overrideCount > 0 ? (
      <Button
        asChild
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
        title="View all active tax overrides"
      >
        <Link to="/dashboard/taxes/overrides">
          <Undo2 size={12} />
          View overrides ({overrideCount})
        </Link>
      </Button>
    ) : null;

  const isAdvanced = viewMode === 'split' || viewMode === 'table';

  // In the default (flat "list") view we expose a single "Advanced" button
  // that drops into the geo drill-down. Once there, the split/table toggle
  // appears alongside a "Basic" button to return to the flat list.
  const ViewControls = isAdvanced ? (
    <>
      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs gap-1.5"
        title="Back to the flat tax list"
        onClick={() => handleSetViewMode('list')}
      >
        <ArrowLeft size={12} />
        Basic
      </Button>
      {/* Split vs. table is a desktop-only distinction — mobile "Advanced"
          always renders the list-panel drill-down (there is no mobile table
          view), so hide the toggle there to avoid a selected icon that doesn't
          match the content. */}
      <div className="hidden md:flex">
        <EntityViewToggle
          value={viewMode}
          onChange={handleSetViewMode}
          options={[
            { value: 'split', icon: Rows2, title: 'Split view' },
            { value: 'table', icon: Table2, title: 'Table view' },
          ]}
        />
      </div>
    </>
  ) : (
    <Button
      size="sm"
      variant="outline"
      className="h-7 px-2 text-xs gap-1.5"
      title="Advanced geo view (states, counties, cities)"
      onClick={() => handleSetViewMode(getStoredAdvancedMode())}
    >
      <SlidersHorizontal size={12} />
      Advanced
    </Button>
  );

  const HeaderActions = (
    <>
      {OverridesLink}
      {/* Tax Policy layer (phase 4) — per-BU / residential-commercial rules. */}
      <Button
        asChild
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs gap-1.5"
        title="Tax rules — who pays tax on what, by business unit and customer segment"
      >
        <Link to="/dashboard/taxes/rules">
          <Scale size={12} />
          Rules
        </Link>
      </Button>
      {ViewControls}
      <Button asChild size="icon-sm" variant="outline" title="Tax Simulator">
        <Link to="/dashboard/taxes/simulator" search={{ zip: undefined }}>
          <Calculator size={14} />
        </Link>
      </Button>
      <Button
        size="icon-sm"
        variant="outline"
        title="Draw service area"
        onClick={() => setPolygonOpen(true)}
      >
        <Pencil size={14} />
      </Button>
      <Button size="icon-sm" title="Look Up ZIP" onClick={() => setLookupOpen(true)}>
        <MapPin size={14} />
      </Button>
      <Button
        size="icon-sm"
        variant="outline"
        title="New manual tax zone (type your own rates)"
        onClick={() => setManualZoneOpen(true)}
      >
        <Plus size={14} />
      </Button>
    </>
  );

  return (
    <>
      <Fullbleed />

      {/* Desktop */}
      <div className="page-enter hidden md:flex h-full min-h-0 gap-3">
        {viewMode === 'list' ? (
          <>
            {/* Flat "all taxes" table — the default view */}
            <div
              key="list"
              className="view-enter flex-1 rounded-xl border bg-card overflow-hidden shadow-sm flex flex-col"
            >
              <EntityIndexHeader title="Taxes" actions={HeaderActions} />
              <TaxBusinessUnitsPanel />
              <TaxTypesTableView groups={groups} zones={zones} />
            </div>
          </>
        ) : viewMode === 'split' ? (
          <>
            {/* Layer 1 — States */}
            <div
              key="split-states"
              className="view-enter flex-1 rounded-xl border bg-card flex flex-col overflow-hidden shadow-sm"
            >
              <EntityIndexHeader title="Taxes" actions={HeaderActions} />
              {pendingZones.length > 0 && (
                <div className="px-3 py-2 border-b bg-yellow-50 dark:bg-yellow-950/20 shrink-0">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-yellow-800 dark:text-yellow-200">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {pendingZones.length} ZIP{pendingZones.length === 1 ? '' : 's'} with pending
                    rate changes
                  </div>
                </div>
              )}
              <TaxZoneListPanel
                selectedStateCode={selectedStateCode}
                selectedCounty={selectedCounty}
                selectedCity={selectedCity}
                onSelectState={handleSelectState}
                searchInput={searchInput}
                onSearchInputChange={setSearchInput}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
              />
            </div>

            {/* Layer 2 — Counties */}
            <div
              key="split-counties"
              className="view-enter flex-1 rounded-xl border bg-card flex flex-col overflow-hidden shadow-sm"
            >
              <TaxCountyPanel
                stateCode={selectedStateCode}
                zones={stateZones}
                groupsById={groupsById}
                selectedCounty={selectedCounty}
                onSelectCounty={handleSelectCounty}
              />
            </div>

            {/* Layer 3 — Cities */}
            <div
              key="split-cities"
              className="view-enter flex-1 rounded-xl border bg-card flex flex-col overflow-hidden shadow-sm"
            >
              <TaxCityPanel
                stateCode={selectedStateCode}
                county={selectedCounty}
                zones={countyZones}
                groupsById={groupsById}
                selectedCity={selectedCity}
                onSelectCity={handleSelectCity}
              />
            </div>
          </>
        ) : (
          <>
            {/* Table card */}
            <div
              key="table"
              className="view-enter flex-1 rounded-xl border bg-card overflow-hidden shadow-sm flex flex-col"
            >
              <EntityIndexHeader title="Taxes" actions={HeaderActions} />
              <TaxZoneTableView
                onOpen={handleOpenDetail}
                selectedStateCode={selectedStateCode}
                selectedCounty={selectedCounty}
                selectedCity={selectedCity}
                onSelectState={handleSelectState}
                onSelectCounty={handleSelectCounty}
                onSelectCity={handleSelectCity}
              />
            </div>
          </>
        )}

        {/* Zone detail sheet — shared across split + table views. Lives
            outside the viewMode ternary so a deep-link / programmatic
            open (e.g. from the table view or a future split-view ZIP
            drill-down) shows the same drawer in either mode. */}
        <Sheet
          open={selectedZoneId !== null}
          onOpenChange={(v) => {
            if (!v) setSelectedZoneId(null);
          }}
        >
          <SheetContent className="w-full sm:max-w-xl p-0 overflow-hidden flex flex-col">
            <TaxZoneDetail
              zoneId={selectedZoneId}
              zones={zones}
              groupsById={groupsById}
              zipsByGroup={zipsByGroup}
              onDelete={() => setSelectedZoneId(null)}
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* Mobile */}
      <div className="md:hidden flex flex-col h-full">
        <EntityIndexHeader title="Taxes" actions={HeaderActions} />
        <div className="flex-1 min-h-0 overflow-hidden">
          {viewMode === 'list' ? (
            <TaxTypesTableView groups={groups} zones={zones} />
          ) : (
            <TaxZoneListPanel
              selectedStateCode={selectedStateCode}
              selectedCounty={selectedCounty}
              selectedCity={selectedCity}
              onSelectState={(code) => {
                handleSelectState(code);
                setMobileCountySheetOpen(true);
              }}
              searchInput={searchInput}
              onSearchInputChange={setSearchInput}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              // Desktop + mobile both mount this panel (the other is
              // CSS-hidden via responsive classes). Without this scope flag
              // both copies emit `state-row-XX` testids, so Playwright's
              // strict-mode locators fail with "matched two elements".
              // Tests run at desktop viewport by default — leave the
              // desktop copy as the canonical testid source and suppress
              // them on the mobile copy.
              emitTestIds={false}
            />
          )}
        </div>

        {/* Mobile county sheet */}
        <Sheet open={mobileCountySheetOpen} onOpenChange={setMobileCountySheetOpen}>
          <SheetContent
            className="w-full sm:max-w-xl p-0 overflow-hidden flex flex-col"
            showCloseButton={false}
          >
            <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setMobileCountySheetOpen(false)}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold">{selectedStateCode} · Counties</span>
            </div>
            <TaxCountyPanel
              stateCode={selectedStateCode}
              zones={stateZones}
              groupsById={groupsById}
              selectedCounty={selectedCounty}
              onSelectCounty={(county) => {
                handleSelectCounty(county);
                setMobileCitySheetOpen(true);
              }}
            />
          </SheetContent>
        </Sheet>

        {/* Mobile city sheet */}
        <Sheet open={mobileCitySheetOpen} onOpenChange={setMobileCitySheetOpen}>
          <SheetContent
            className="w-full sm:max-w-xl p-0 overflow-hidden flex flex-col"
            showCloseButton={false}
          >
            <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
              <Button variant="ghost" size="icon-sm" onClick={() => setMobileCitySheetOpen(false)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-semibold">
                {selectedCounty && isProvincialCountyLabel(selectedCounty)
                  ? `${selectedStateCode} · Cities`
                  : `${selectedCounty} County · Cities`}
              </span>
            </div>
            <TaxCityPanel
              stateCode={selectedStateCode}
              county={selectedCounty}
              zones={countyZones}
              groupsById={groupsById}
              selectedCity={selectedCity}
              onSelectCity={handleSelectCity}
            />
          </SheetContent>
        </Sheet>
      </div>

      <TaxZipLookupDialog open={lookupOpen} onOpenChange={setLookupOpen} />
      <PolygonTaxZoneDialog open={polygonOpen} onOpenChange={setPolygonOpen} />
      <ManualTaxZoneDialog open={manualZoneOpen} onOpenChange={setManualZoneOpen} />
    </>
  );
}
